import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Zap, 
  MessageSquare, 
  Database, 
  GitBranch, 
  Cloud, 
  CheckCircle2,
  Loader2,
  ArrowRight,
  Bot
} from 'lucide-react';
import { SiSlack, SiAmazonwebservices } from 'react-icons/si';

// Animated typing effect
const TypeWriter = ({ text, delay = 0 }) => {
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentIndex < text.length) {
        setDisplayText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }
    }, delay + currentIndex * 30);
    return () => clearTimeout(timer);
  }, [currentIndex, text, delay]);

  return <span>{displayText}<span className="animate-pulse">|</span></span>;
};

// Input Streams Card
const InputStreamsCard = () => {
  const [active, setActive] = useState(false);
  
  useEffect(() => {
    const interval = setInterval(() => setActive(prev => !prev), 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
      className="p-6 rounded-2xl bg-slate-900/60 border border-emerald-500/20 backdrop-blur-sm"
    >
      <div className="flex items-center gap-2 mb-4">
        <Zap className="w-5 h-5 text-emerald-400" />
        <span className="font-semibold text-white">Input Streams</span>
      </div>
      
      <motion.div
        animate={{ opacity: active ? 1 : 0.6, scale: active ? 1 : 0.98 }}
        className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-pink-500/20 flex items-center justify-center">
            <SiSlack className="w-5 h-5 text-pink-400" />
          </div>
          <div>
            <div className="text-sm font-medium text-white">Slack Message</div>
            <div className="text-xs text-slate-500">"Client requested API docs..."</div>
          </div>
        </div>
        {active && (
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: '100%' }}
            transition={{ duration: 2 }}
            className="h-0.5 bg-gradient-to-r from-pink-500 to-emerald-500 mt-3 rounded-full"
          />
        )}
      </motion.div>
    </motion.div>
  );
};

// Multi-Step Workflow Card
const MultiStepWorkflowCard = () => {
  const [progress, setProgress] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(prev => (prev >= 100 ? 0 : prev + 2));
    }, 100);
    return () => clearInterval(interval);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
      viewport={{ once: true }}
      className="p-6 rounded-2xl bg-slate-900/60 border border-emerald-500/20 backdrop-blur-sm"
    >
      <div className="flex items-center gap-2 mb-4">
        <GitBranch className="w-5 h-5 text-cyan-400" />
        <span className="font-semibold text-white">Multi-Step Workflow</span>
      </div>
      
      <p className="text-sm text-slate-400 mb-6">
        The agent autonomously updates CRM records based on the conversation context.
      </p>
      
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-slate-800/80 border border-slate-700/50 flex items-center justify-center">
          <Cloud className="w-6 h-6 text-slate-500" />
        </div>
        
        <div className="flex-1 h-1 bg-slate-800 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-cyan-500 to-emerald-500 rounded-full"
            style={{ width: `${progress}%` }}
          />
        </div>
        
        <div className="w-12 h-12 rounded-xl bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center">
          <GitBranch className="w-6 h-6 text-cyan-400" />
        </div>
      </div>
    </motion.div>
  );
};

// Execution Layer Card
const ExecutionLayerCard = () => {
  const [status, setStatus] = useState('fetching');
  
  useEffect(() => {
    const interval = setInterval(() => {
      setStatus(prev => prev === 'fetching' ? 'complete' : 'fetching');
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      viewport={{ once: true }}
      className="p-6 rounded-2xl bg-slate-900/60 border border-emerald-500/20 backdrop-blur-sm"
    >
      <div className="flex items-center gap-2 mb-4">
        <Database className="w-5 h-5 text-orange-400" />
        <span className="font-semibold text-white">Execution Layer</span>
      </div>
      
      <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
            <SiAmazonwebservices className="w-5 h-5 text-orange-400" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium text-white">AWS S3</div>
            <div className="text-xs text-slate-500 flex items-center gap-2">
              {status === 'fetching' ? (
                <>
                  <Loader2 className="w-3 h-3 animate-spin text-orange-400" />
                  <span>Fetching /docs/api-v2.pdf</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                  <span className="text-emerald-400">Complete</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Vrittaa Reasoning Core Card
const ReasoningCoreCard = () => {
  const logs = [
    { prefix: '~', text: 'Analyzing input intent ...', color: 'text-slate-400' },
    { prefix: '✓', text: 'Intent: Documentation Request', color: 'text-emerald-400' },
    { prefix: '~', text: 'Querying AWS docs bucket ...', color: 'text-slate-400' },
    { prefix: '_', text: 'Generating synthesized response ...', color: 'text-slate-400' },
  ];

  const [visibleLogs, setVisibleLogs] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisibleLogs(prev => (prev >= logs.length ? 0 : prev + 1));
    }, 1500);
    return () => clearInterval(interval);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.3 }}
      viewport={{ once: true }}
      className="p-6 rounded-2xl bg-slate-900/60 border border-purple-500/20 backdrop-blur-sm row-span-2"
    >
      <div className="flex flex-col items-center mb-6">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center mb-4">
          <Bot className="w-8 h-8 text-white" />
        </div>
        <span className="font-bold text-xl text-white">Vrittaa Reasoning Core</span>
      </div>
      
      <div className="p-4 rounded-xl bg-slate-950/80 border border-slate-800 font-mono text-sm space-y-2 min-h-[160px]">
        {logs.slice(0, visibleLogs).map((log, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className={`flex gap-2 ${log.color}`}
          >
            <span>{log.prefix}</span>
            <span>{log.text}</span>
          </motion.div>
        ))}
        {visibleLogs < logs.length && (
          <div className="flex gap-2 text-slate-600">
            <span>_</span>
            <span className="animate-pulse">Processing...</span>
          </div>
        )}
      </div>
    </motion.div>
  );
};

const WorkflowVisualizer = () => {
  return (
    <section
      data-testid="workflow-visualizer"
      className="relative py-24 lg:py-32 px-6"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="uppercase tracking-[0.2em] text-xs font-semibold text-emerald-400 mb-4 block">
            Live Workflow Demo
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-outfit text-white mb-4">
            Watch Agents <span className="text-gradient">Think in Real-Time</span>
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            See how Vrittaa AI processes inputs, reasons through context, and executes actions autonomously.
          </p>
        </motion.div>

        {/* Workflow Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            <InputStreamsCard />
            <MultiStepWorkflowCard />
            <ExecutionLayerCard />
          </div>

          {/* Right Column - Reasoning Core */}
          <div className="lg:col-span-2 flex items-center justify-center">
            <div className="w-full max-w-md">
              <ReasoningCoreCard />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WorkflowVisualizer;
